import type { RouteLocationNormalized } from 'vue-router';
import { useMenuStore, useSettingsStore, useDocumentStore } from '../..';
import { isString } from '../../../utils/is';
import type { TabProps } from './types';
import { defineStore } from 'pinia';
import { ref, computed, reactive, onMounted } from 'vue';
import { bus } from '@sfvine/main-qiankun';
import { useRouter } from 'vue-router';
import { getEmpCode } from '../../../utils/auth';
import { equalToRoute, getDecodePathname } from '../../../utils/url';
import { useFavoritesCacheStore } from '../favorites/cache';

const formatTag = (
  route: RouteLocationNormalized,
  routeSettings: {
    baseOn?: ('frontend' | 'filesystem') | undefined;
    baseURL?: string | undefined;
    homeTitle?: string | undefined;
    homePath?: string | undefined;
    getNotFoundTabTitle?: ((route: RouteLocationNormalized) => string) | undefined;
  },
): TabProps => {
  const { name, meta, fullPath, path, query } = route;
  // 若是子应用，去菜单数据获取名称
  let subAppTitle = '';
  if (meta.isSubApp || name === 'notFound' || !meta.title || meta.title === '404') {
    const menuStore = useMenuStore();
    const favoritesCache = useFavoritesCacheStore()
    const decodePath = getDecodePathname(route.path)

    subAppTitle = menuStore.flatMenuObj[decodePath]?.title || favoritesCache.data[decodePath]?.title || '';
    if (!subAppTitle) {
      if (routeSettings?.getNotFoundTabTitle) {
        subAppTitle = routeSettings?.getNotFoundTabTitle(route);
      } else {
        subAppTitle = (query?.title as string) || ''; // 当未查找到时（如第三方跳转页面，菜单中不存在时），使用路由中的名称
      }
    }
  }

  return {
    title: subAppTitle || (meta.title as string) || '',
    name: String(name),
    fullPath,
    path,
    query,
    ignoreCache: meta.ignoreCache as boolean,
  };
};

const useTabBarStore = defineStore(
  'tabBar',
  () => {
    const settingsStore = useSettingsStore();
    const { setDocumentTitle } = useDocumentStore()
    const router = useRouter();
    const menuStore = useMenuStore();

    const DEFAULT_HOME_TITLE = '工作台'; // 默认标题
    const homeTitle = computed(() => settingsStore.settings.route.homeTitle || DEFAULT_HOME_TITLE);
    const homeTitleFromSubApp = ref(''); // 子应用 homePath 标题
    const homePath = computed(() => settingsStore.settings.route.homePath || '/');

    // 默认固定tab
    const DEFAULT_ROUTE = computed<TabProps>(() => {
      return {
        title: homeTitleFromSubApp.value || homeTitle.value,
        name: 'workplace',
        fullPath: homePath.value,
        path: new URL(`http://localhost${homePath.value}`).pathname,
      };
    });

    const { name, meta } = router.currentRoute.value
    const defaultCacheTabList = typeof name === 'string' && !meta.ignoreCache && !['noFound', '404'].includes(name) ? [name] : []
    const cacheTabList = reactive(new Set(defaultCacheTabList));
    const tagList = ref<TabProps[]>([DEFAULT_ROUTE.value]);
    const tagObjByUpdateTab = ref<Record<string, TabProps>>({})
    const tagListByShow = computed(() =>
      tagList.value.map((item) => {
        const v = menuStore.flatMenuObj[item.path]; // 避免判断path完全相等，存在参数不同的情况
        const target = tagObjByUpdateTab.value[item.path]
        return {
          ...item,
          /** 用户手动更新的 tag 优先级最高 */
          title: target?.title || v?.title || item.title,
        };
      }),
    );

    function addTabList(target: TabProps, ignoreCache?: boolean) {
      // 忽略 query 等查询参数，以 path 为准，避免 cas 登录后出现 2 个一模一样的 tab 的情况
      // 后期可以考虑加配置来关闭此功能
      const index = tagList.value.findIndex((tag) => equalToRoute(tag.path, undefined, target.path));
      if (index > -1) {
        cacheTabList.delete(tagList.value[index].name);
        tagList.value[index] = { ...target, title: target.title === '' ? tagList.value[index].title : target.title };
      } else {
        tagList.value.push(target);
      }

      if (!ignoreCache) {
        cacheTabList.add(target.name as string);
      }

      // 不开启 tabbar 的情况下无法自动更新 title，后期需要解决
      // 更新 title
      if (target.title) {
        setDocumentTitle(target.title)
      }
    }

    function updateTabList(route: RouteLocationNormalized) {
      if (
        route.meta.noAffix ||
        route.meta.isLink
        // || tagList.value.some((tag) => tag.fullPath === route.fullPath) // title 可能发生变化，例如工作台，因此这里不能直接 return
      ) {
        return;
      }

      const target = formatTag(route, settingsStore.settings.route);

      // path 为 homepath 路径时需要更新 title
      if (route.path === DEFAULT_ROUTE.value.path) {
        if (DEFAULT_ROUTE.value.title === DEFAULT_HOME_TITLE) {
          // 如果 title 为默认工作台，则将 title 更新为 path 对应的 title
          homeTitleFromSubApp.value = target.title;
        } else {
          // 否则保持原样（例如 homePath 为效益看板地址，这个时候又不想显示效益看板，想显示为首页）
          target.title = DEFAULT_ROUTE.value.title;
        }
      }

      addTabList(target, !!route.meta.ignoreCache);
    }
    function deleteTag(idx: number) {
      const oldTabList = tagList.value.slice();
      const target = tagList.value.splice(idx, 1)[0];
      cacheTabList.delete(target?.name);
      bus.$emit('afterUpdateTab', { type: 'delete', tab: target, tabList: tagList.value, oldTabList });
    }

    /**
     * !用户侧 api，请谨慎修改入参及返回
     * 删除 tag
     * @param path 需要删除的路径
     */
    function deleteTagForUser(path: string) {
      const index = tagList.value.findIndex((tag) => equalToRoute(tag.path, undefined, path));
      if (index < 0) {
        throw new Error(`未找到 ${path} 路径`);
      }

      deleteTag(index);
    }

    /**
     * 清空所有 tag
     */
    function clearTabList() {
      resetTabList();
      router.push(tagList.value[0].fullPath);
    }

    /**
     * !用户侧 api，请谨慎修改入参及返回
     * 清空所有 tag
     */
    function clearTabListForUser() {
      clearTabList()
    }

    function addCache(name: string) {
      if (isString(name) && name !== '') cacheTabList.add(name);
    }
    function deleteCache(tag: TabProps) {
      cacheTabList.delete(tag.name);
    }
    function freshTabList(tags: TabProps[]) {
      const oldTabList = tagList.value.slice();
      tagList.value = tags;
      cacheTabList.clear();
      // 要先判断ignoreCache
      tagList.value
        .filter((el) => !el.ignoreCache)
        .map((el) => el.name)
        .forEach((x) => cacheTabList.add(x));
      bus.$emit('afterUpdateTab', { type: 'fresh', tab: null, tabList: tagList.value, oldTabList });
    }
    function resetTabList() {
      const oldTabList = tagList.value.slice();
      tagList.value = [DEFAULT_ROUTE.value];
      cacheTabList.clear();
      cacheTabList.add(DEFAULT_ROUTE.value.name);
      router.push(DEFAULT_ROUTE.value.fullPath);
      bus.$emit('afterUpdateTab', { type: 'reset', tab: null, tabList: tagList.value, oldTabList });
    }

    onMounted(() => {
      bus.$on(
        'updateTab',
        ({ type, tag }: { type: '' | 'add' | 'update' | 'delete' | 'reset'; tag?: TabProps } = { type: '' }) => {
          if (type === 'reset') {
            resetTabList();
            return;
          }
          if (!tag) {
            return;
          }

          if (type === 'add' || type === 'update') {
            tagObjByUpdateTab.value[tag.path] = tag
            addTabList(tag);
            return;
          }
          if (type === 'delete') {
            const idx = tagList.value.findIndex((x) => x.fullPath === tag.fullPath);
            const latest = tagList.value[idx - 1]; // 获取队列的前一个tab
            deleteTag(idx);
            if (latest) {
              router.push(latest.fullPath);
            }
            return;
          }
        },
      );
    });

    return {
      tagListByShow,
      tagList,
      tagObjByUpdateTab,
      cacheTabList,
      updateTabList,
      deleteTag,
      deleteTagForUser,
      clearTabList,
      clearTabListForUser,
      addCache,
      deleteCache,
      freshTabList,
      resetTabList,
    };
  },
  {
    // @ts-ignore
    get persist() {
      const {
        settings: { system, tabBar },
      } = useSettingsStore();
      let key = getEmpCode();
      if (tabBar.ignoreTabBarPersistent || !key || !system || !tabBar) {
        return false;
      }

      key = `${system.systemCode || `${system.webTitle}-${system.webSubTitle}`}-${key}`;
      const storage = tabBar.cacheType === 'sessionStorage' ? sessionStorage : localStorage;
      return { key, storage };
    },
  },
);

export default useTabBarStore;
